import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LogDto, UserDto } from '../../shared/api';
import { COLUMN_KINDS, LISTS, LIST_LABEL, PASSWORD_MIN, ROLE_LABEL, colList, type ColumnKind, type Role } from '../../shared/domain';
import { del, get, patch, post, queryClient, refresh } from '../api';
import { Err, Modal, errMsg, useApp } from '../components/ui';
import { LogTable } from '../components/dialogs';

type Tab = 'account' | 'users' | 'lists' | 'columns' | 'activity';

/** Runs a change and reports the result inside the panel. */
function useAct() {
  const { toast } = useApp();
  const [err, setErr] = useState('');
  const act = async (fn: () => Promise<unknown>, msg: string) => {
    setErr('');
    try {
      await fn();
      await refresh();
      toast(msg);
      return true;
    } catch (e) {
      setErr(errMsg(e));
      return false;
    }
  };
  return { err, act };
}

export function PasswordForm({ forced }: { forced?: boolean }) {
  const { err, act } = useAct();
  const [old, setOld] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const ok = pw.length >= PASSWORD_MIN && pw === pw2 && !!old;
  return (
    <div className="fgrid one narrow">
      <label className="field">
        {forced ? 'Your old PIN or password' : 'Current password'}
        <input type="password" autoComplete="current-password" value={old} onChange={(e) => setOld(e.target.value)} />
      </label>
      <label className="field">
        New password (at least {PASSWORD_MIN} characters)
        <input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
      </label>
      <label className="field">
        New password again
        <input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
      </label>
      {pw2 && pw !== pw2 && <p className="meta">The two new passwords are not the same.</p>}
      <button
        className="btn primary"
        disabled={!ok}
        onClick={() =>
          void act(() => post('/api/me/password', { old, password: pw }), 'Password changed').then((done) => {
            if (done) (setOld(''), setPw(''), setPw2(''));
          })
        }
      >
        Change password
      </button>
      <Err e={err} />
    </div>
  );
}

function Account() {
  const { d } = useApp();
  const logout = async () => {
    await post('/api/logout').catch(() => undefined);
    await queryClient.resetQueries();
  };
  return (
    <>
      <p>
        Signed in as <b>{d.me.name}</b> ({ROLE_LABEL[d.me.role]}).
      </p>
      <button className="btn" onClick={() => void logout()}>
        Sign out
      </button>
      <h4>Change password</h4>
      <PasswordForm />
    </>
  );
}

function Users() {
  const { d } = useApp();
  const users = useQuery({ queryKey: ['users'], queryFn: () => get<UserDto[]>('/api/users') });
  const { err, act } = useAct();
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [role, setRole] = useState<Role>('user');
  return (
    <>
      <p className="meta">Admins can change everything. Users can only view.</p>
      <div className="scroll">
        <table className="mini">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(users.data ?? []).map((u) => (
              <tr key={u.id}>
                <td>
                  <b>{u.name}</b>
                  {u.id === d.me.id && <span className="meta"> (you)</span>}
                </td>
                <td>
                  <select value={u.role} onChange={(e) => void act(() => patch(`/api/users/${u.id}`, { role: e.target.value }), `${u.name} is now ${ROLE_LABEL[e.target.value as Role]}`)}>
                    <option value="admin">{ROLE_LABEL.admin}</option>
                    <option value="user">{ROLE_LABEL.user}</option>
                  </select>
                </td>
                <td>{u.active ? 'Active' : <span className="tag bad">Deactivated</span>}</td>
                <td className="nowrap">
                  <button
                    className="btn small"
                    onClick={() => {
                      const p = prompt(`New password for ${u.name} (at least ${PASSWORD_MIN} characters). They must change it at the next sign-in.`);
                      if (p) void act(() => patch(`/api/users/${u.id}`, { password: p }), `Password of ${u.name} reset`);
                    }}
                  >
                    Reset password
                  </button>{' '}
                  {u.id !== d.me.id && (
                    <button className="btn small" onClick={() => void act(() => patch(`/api/users/${u.id}`, { active: !u.active }), u.active ? `${u.name} deactivated` : `${u.name} activated`)}>
                      {u.active ? 'Deactivate' : 'Activate'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h4>Add a user</h4>
      <div className="row">
        <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <input type="password" autoComplete="new-password" placeholder={`Password (min. ${PASSWORD_MIN})`} value={pw} onChange={(e) => setPw(e.target.value)} />
        <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
          <option value="user">{ROLE_LABEL.user}</option>
          <option value="admin">{ROLE_LABEL.admin}</option>
        </select>
        <button
          className="btn primary"
          disabled={!name.trim() || pw.length < PASSWORD_MIN}
          onClick={() =>
            void act(() => post('/api/users', { name, password: pw, role }), `${name} added`).then((ok) => {
              if (ok) (setName(''), setPw(''), void users.refetch());
            })
          }
        >
          Add user
        </button>
      </div>
      <Err e={err} />
    </>
  );
}

function OptionList({ list, label }: { list: string; label: string }) {
  const { d } = useApp();
  const { err, act } = useAct();
  const [v, setV] = useState('');
  const values = d.b.options[list] ?? [];
  return (
    <div className="optlist">
      <h4>{label}</h4>
      <ul>
        {values.map((x) => (
          <li key={x}>
            <span>{x}</span>
            <button
              className="btn small"
              onClick={() => {
                const to = prompt(`Rename “${x}” to (every row with it changes too):`, x);
                if (to && to.trim() && to !== x) void act(() => post('/api/options/rename', { list, from: x, to }), 'Renamed');
              }}
            >
              Rename
            </button>
            <button className="btn small danger" onClick={() => confirm(`Remove “${x}” from ${label}?`) && void act(() => post('/api/options/delete', { list, value: x }), 'Removed')}>
              Remove
            </button>
          </li>
        ))}
        {!values.length && <li className="meta">No options yet.</li>}
      </ul>
      <div className="row">
        <input className="grow" placeholder={`New ${label.toLowerCase()} option`} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && v.trim() && void act(() => post('/api/options', { list, value: v }), 'Added').then((ok) => ok && setV(''))} />
        <button className="btn" disabled={!v.trim()} onClick={() => void act(() => post('/api/options', { list, value: v }), 'Added').then((ok) => ok && setV(''))}>
          + Add
        </button>
      </div>
      <Err e={err} />
    </div>
  );
}

function Lists() {
  const { d } = useApp();
  return (
    <>
      <p className="meta">The choices of each dropdown column. New values typed in the sheet are added here automatically. QC and Installation have fixed choices.</p>
      <div className="optgrid">
        {LISTS.map((l) => (
          <OptionList key={l} list={l} label={LIST_LABEL[l]} />
        ))}
        {d.b.columns
          .filter((c) => c.kind === 'dropdown')
          .map((c) => (
            <OptionList key={c.id} list={colList(c.id)} label={c.label} />
          ))}
      </div>
    </>
  );
}

const KIND_LABEL: Record<ColumnKind, string> = { text: 'Text', number: 'Number', dropdown: 'Dropdown' };

function Columns() {
  const { d } = useApp();
  const { err, act } = useAct();
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<ColumnKind>('text');
  return (
    <>
      <p className="meta">Fixed columns: Name, Place, Type, QC, Project, Installation, Resource, QTY, Used. Add your own columns here.</p>
      <ul className="collist">
        {d.b.columns.map((c) => (
          <li key={c.id}>
            <b>{c.label}</b> <span className="meta">{KIND_LABEL[c.kind]}</span>
            <span className="grow" />
            <button
              className="btn small"
              onClick={() => {
                const to = prompt('New column name:', c.label);
                if (to && to.trim() && to !== c.label) void act(() => patch(`/api/columns/${c.id}`, { label: to }), 'Column renamed');
              }}
            >
              Rename
            </button>
            <button className="btn small danger" onClick={() => confirm(`Delete the column ${c.label} and its values in every row?`) && void act(() => del(`/api/columns/${c.id}`), 'Column deleted')}>
              Delete
            </button>
          </li>
        ))}
        {!d.b.columns.length && <li className="meta">No extra columns yet.</li>}
      </ul>
      <div className="row">
        <input placeholder="Column name" value={label} onChange={(e) => setLabel(e.target.value)} />
        <select value={kind} onChange={(e) => setKind(e.target.value as ColumnKind)}>
          {COLUMN_KINDS.map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </select>
        <button className="btn primary" disabled={!label.trim()} onClick={() => void act(() => post('/api/columns', { label, kind }), `Column ${label} added`).then((ok) => ok && setLabel(''))}>
          + Add column
        </button>
      </div>
      <Err e={err} />
    </>
  );
}

function Activity() {
  const log = useQuery({ queryKey: ['log-all'], queryFn: () => get<LogDto[]>('/api/log?limit=300') });
  const [q, setQ] = useState('');
  const t = q.trim().toLowerCase();
  const list = (log.data ?? []).filter((l) => !t || [l.user, l.action, l.itemName, l.project, l.reason, l.details].some((v) => v.toLowerCase().includes(t)));
  return (
    <>
      <input className="search" type="search" placeholder="Filter by item, project, user …" value={q} onChange={(e) => setQ(e.target.value)} />
      <p className="meta">The latest 300 changes. Every item’s full history is in its Details.</p>
      <LogTable list={list} loading={log.isLoading} showItem />
    </>
  );
}

export function Settings({ onClose, start = 'account' }: { onClose: () => void; start?: Tab }) {
  const { d } = useApp();
  const [tab, setTab] = useState<Tab>(start);
  const tabs: [Tab, string][] = [['account', 'My account'], ...(d.isAdmin ? ([['users', 'Users'], ['lists', 'Dropdown lists'], ['columns', 'Columns']] as [Tab, string][]) : []), ['activity', 'Activity log']];
  return (
    <Modal title="Settings" onClose={onClose} wide>
      <div className="tabs" role="tablist">
        {tabs.map(([t, l]) => (
          <button key={t} role="tab" aria-selected={tab === t} className={`btn ${tab === t ? 'on' : ''}`} onClick={() => setTab(t)}>
            {l}
          </button>
        ))}
      </div>
      {tab === 'account' && <Account />}
      {tab === 'users' && d.isAdmin && <Users />}
      {tab === 'lists' && d.isAdmin && <Lists />}
      {tab === 'columns' && d.isAdmin && <Columns />}
      {tab === 'activity' && <Activity />}
    </Modal>
  );
}
